import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDownIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

function RuleSelector({
  title,
  rules,
  selectedRules,
  onRuleToggle,
  onToggleAll,
  loading,
}) {
  const [expandedRule, setExpandedRule] = useState(null);

  if (loading) {
    return (
      <div className="card p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-10 bg-neutral-200 dark:bg-neutral-700 rounded"></div>
            <div className="h-10 bg-neutral-200 dark:bg-neutral-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!rules || rules.length === 0) {
    return null;
  }

  const allSelected = selectedRules.length === rules.length;

  const toggleRuleExpansion = (ruleId, e) => {
    e.stopPropagation();
    setExpandedRule(expandedRule === ruleId ? null : ruleId);
  };

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
          {title}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleAll(true)}
            disabled={allSelected}
            className={`text-xs px-2 py-1 rounded transition-colors ${
              allSelected
                ? 'text-neutral-400 dark:text-neutral-600 cursor-not-allowed'
                : 'text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20'
            }`}
          >
            Select All
          </button>
          <span className="text-neutral-300 dark:text-neutral-700">|</span>
          <button
            onClick={() => onToggleAll(false)}
            disabled={selectedRules.length === 0}
            className={`text-xs px-2 py-1 rounded transition-colors ${
              selectedRules.length === 0
                ? 'text-neutral-400 dark:text-neutral-600 cursor-not-allowed'
                : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
            }`}
          >
            Deselect All
          </button>
        </div>
      </div>

      <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
        {selectedRules.length} of {rules.length} rules selected
      </p>

      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {rules.map((rule) => {
          const isSelected = selectedRules.includes(rule.id);
          const isExpanded = expandedRule === rule.id;

          return (
            <div
              key={rule.id}
              className={`border rounded-lg transition-all ${
                isSelected
                  ? 'border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/20'
                  : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900'
              }`}
            >
              <div
                className="flex items-center p-3 cursor-pointer"
                onClick={() => onRuleToggle(rule.id)}
              >
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-3 flex-shrink-0 transition-colors ${
                    isSelected
                      ? 'bg-primary-600 border-primary-600'
                      : 'border-neutral-300 dark:border-neutral-600'
                  }`}
                >
                  {isSelected && (
                    <CheckCircleIcon className="w-4 h-4 text-white" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                    {rule['Rule Name']}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                    {rule.id}
                  </p>
                </div>

                <button
                  onClick={(e) => toggleRuleExpansion(rule.id, e)}
                  className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded transition-colors ml-2"
                >
                  <motion.div
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDownIcon className="w-4 h-4 text-neutral-400" />
                  </motion.div>
                </button>
              </div>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-3 pb-3 pt-0 border-t border-neutral-100 dark:border-neutral-800">
                      <div className="space-y-2 pt-3">
                        {rule['Rule Definition'] && (
                          <div>
                            <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                              Definition
                            </p>
                            <p className="text-xs text-neutral-700 dark:text-neutral-300">
                              {rule['Rule Definition']}
                            </p>
                          </div>
                        )}

                        {rule['Rule Elaboration'] && (
                          <div>
                            <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                              Elaboration
                            </p>
                            <p className="text-xs text-neutral-700 dark:text-neutral-300">
                              {rule['Rule Elaboration']}
                            </p>
                          </div>
                        )}

                        {rule['Rule Examples'] && (
                          <div>
                            <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                              Examples
                            </p>
                            <pre className="text-xs text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap font-mono bg-neutral-50 dark:bg-neutral-800 p-2 rounded">
                              {rule['Rule Examples']}
                            </pre>
                          </div>
                        )}

                        {rule['Rule Exceptions and Relationships'] && (
                          <div>
                            <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                              Exceptions
                            </p>
                            <p className="text-xs text-neutral-700 dark:text-neutral-300">
                              {rule['Rule Exceptions and Relationships']}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RuleSelector;
